import React from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    useColorModeValue,
    List,
    ListItem,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const Privacy = () => {
    const bg = useColorModeValue("#FAFAFA", "gray.800");

    return (
        <Box bg={bg} minHeight="100vh">
            <NavbarWithCallToAction />

            <Container maxW="800px" py={12}>
                <VStack spacing={8} align="stretch">
                    <Box textAlign="center" mb={8}>
                        <Heading as="h1" size="2xl" mb={4} color="red.700">
                            Privacy Policy
                        </Heading>
                        <Text fontSize="lg" color="gray.600">
                            Last updated: January 2024
                        </Text>
                    </Box>

                    <Box>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            SurgReady ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, website, and related services.
                        </Text>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            This policy complies with the Health Insurance Portability and Accountability Act (HIPAA), General Data Protection Regulation (GDPR), and other applicable privacy laws.
                        </Text>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            1. Information We Collect
                        </Heading>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            Personal Information
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We collect information you provide directly to us, including:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Name, email address, phone number, and contact information</ListItem>
                            <ListItem color="gray.700">• Date of birth and demographic information</ListItem>
                            <ListItem color="gray.700">• Account credentials and authentication information</ListItem>
                            <ListItem color="gray.700">• Payment and billing information</ListItem>
                        </List>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            Health Information
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            With your consent, we may collect health-related information including:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Surgery type, date, and related medical information</ListItem>
                            <ListItem color="gray.700">• Medical history and current health status</ListItem>
                            <ListItem color="gray.700">• Medications and treatment information</ListItem>
                            <ListItem color="gray.700">• Progress tracking and assessment data</ListItem>
                            <ListItem color="gray.700">• Healthcare provider information (with consent)</ListItem>
                        </List>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            Technical Information
                        </Heading>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Device information and browser type</ListItem>
                            <ListItem color="gray.700">• IP address and location data</ListItem>
                            <ListItem color="gray.700">• Usage patterns and platform interactions</ListItem>
                            <ListItem color="gray.700">• Cookies and similar tracking technologies</ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            2. How We Use Your Information
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We use the information we collect to:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Provide and personalize our educational services</ListItem>
                            <ListItem color="gray.700">• Create customized preparation plans and recommendations</ListItem>
                            <ListItem color="gray.700">• Track your progress and send reminders</ListItem>
                            <ListItem color="gray.700">• Facilitate communication with your healthcare providers (with consent)</ListItem>
                            <ListItem color="gray.700">• Process payments and manage your account</ListItem>
                            <ListItem color="gray.700">• Send important updates and notifications</ListItem>
                            <ListItem color="gray.700">• Improve our platform and develop new features</ListItem>
                            <ListItem color="gray.700">• Comply with legal obligations and protect our rights</ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            3. Information Sharing and Disclosure
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                        </Text>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            With Your Consent
                        </Heading>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Healthcare providers you authorize to access your progress</ListItem>
                            <ListItem color="gray.700">• Family members or caregivers you designate</ListItem>
                        </List>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            Service Providers
                        </Heading>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Cloud hosting and data storage providers</ListItem>
                            <ListItem color="gray.700">• Payment processing services</ListItem>
                            <ListItem color="gray.700">• Customer support and communication platforms</ListItem>
                            <ListItem color="gray.700">• Analytics and performance monitoring services</ListItem>
                        </List>

                        <Heading as="h3" size="md" mb={3} color="red.600">
                            Legal Requirements
                        </Heading>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• When required by law or legal process</ListItem>
                            <ListItem color="gray.700">• To protect the rights and safety of our users</ListItem>
                            <ListItem color="gray.700">• In connection with a business transaction or reorganization</ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            4. Data Security
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We implement comprehensive security measures to protect your information:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• End-to-end encryption for data transmission and storage</ListItem>
                            <ListItem color="gray.700">• Multi-factor authentication and access controls</ListItem>
                            <ListItem color="gray.700">• Regular security audits and vulnerability assessments</ListItem>
                            <ListItem color="gray.700">• HIPAA-compliant infrastructure and procedures</ListItem>
                            <ListItem color="gray.700">• Employee training on privacy and security best practices</ListItem>
                            <ListItem color="gray.700">• Incident response and breach notification procedures</ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            5. Data Retention
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We retain your information for as long as necessary to provide our services and comply with legal obligations:
                        </Text>

                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>Data Type</Th>
                                        <Th>Retention Period</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    <Tr>
                                        <Td>Account Information</Td>
                                        <Td>Until account deletion + 30 days</Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Health Information</Td>
                                        <Td>7 years after last activity</Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Payment Records</Td>
                                        <Td>7 years for tax purposes</Td>
                                    </Tr>
                                    <Tr>
                                        <Td>Usage Analytics</Td>
                                        <Td>2 years (anonymized)</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            6. Your Rights and Choices
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            You have the following rights regarding your personal information:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• <strong>Access:</strong> Request a copy of your personal information</ListItem>
                            <ListItem color="gray.700">• <strong>Correction:</strong> Update or correct inaccurate information</ListItem>
                            <ListItem color="gray.700">• <strong>Deletion:</strong> Request deletion of your personal information</ListItem>
                            <ListItem color="gray.700">• <strong>Portability:</strong> Export your data in a machine-readable format</ListItem>
                            <ListItem color="gray.700">• <strong>Restriction:</strong> Limit how we process your information</ListItem>
                            <ListItem color="gray.700">• <strong>Objection:</strong> Object to certain processing activities</ListItem>
                            <ListItem color="gray.700">• <strong>Withdrawal:</strong> Withdraw consent at any time</ListItem>
                        </List>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            To exercise these rights, contact us at privacy@surgready.com.
                        </Text>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            7. Cookies and Tracking Technologies
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We use cookies and similar technologies to enhance your experience:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• <strong>Essential cookies:</strong> Required for platform functionality</ListItem>
                            <ListItem color="gray.700">• <strong>Analytics cookies:</strong> Help us understand usage patterns</ListItem>
                            <ListItem color="gray.700">• <strong>Preference cookies:</strong> Remember your settings and preferences</ListItem>
                        </List>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            You can control cookies through your browser settings, though some features may not work properly if cookies are disabled.
                        </Text>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            8. International Data Transfers
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for international transfers, including:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Adequate data protection agreements with service providers</ListItem>
                            <ListItem color="gray.700">• Standard contractual clauses approved by regulatory authorities</ListItem>
                            <ListItem color="gray.700">• Compliance with applicable international privacy frameworks</ListItem>
                        </List>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            9. Changes to This Privacy Policy
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
                        </Text>
                        <List spacing={2} mb={4}>
                            <ListItem color="gray.700">• Posting the updated policy on our website</ListItem>
                            <ListItem color="gray.700">• Sending email notifications to registered users</ListItem>
                            <ListItem color="gray.700">• Displaying prominent notices on our platform</ListItem>
                        </List>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
                        </Text>
                    </Box>

                    <Divider />

                    <Box>
                        <Heading as="h2" size="lg" mb={4} color="red.700">
                            10. Contact Information
                        </Heading>
                        <Text mb={4} lineHeight="tall" color="gray.700">
                            If you have questions about this Privacy Policy or our privacy practices, please contact us:
                        </Text>
                        <Text mb={2} color="gray.700">
                            <strong>Privacy Officer:</strong> privacy@surgready.com
                        </Text>
                        <Text mb={2} color="gray.700">
                            <strong>Data Protection Officer:</strong> dpo@surgready.com
                        </Text>
                        <Text mb={2} color="gray.700">
                            <strong>Address:</strong> 123 Medical Center Drive, Suite 456, Healthcare City, HC 12345
                        </Text>
                        <Text mb={4} color="gray.700">
                            <strong>Phone:</strong> 1-800-SURGREADY
                        </Text>
                    </Box>

                    <Box bg="blue.50" p={6} borderRadius="md" mt={8}>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                            This Privacy Policy is effective as of January 2024 and supersedes all previous versions.
                            We are committed to protecting your privacy and maintaining the security of your personal information.
                        </Text>
                    </Box>
                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default Privacy; 